import { _GLOBALS } from '../globals.js';
import { logit } from "./simpleLog.js";
import { WalletConnectError, WalletNotFoundError } from '../Errors.js';
import { detectPlatform } from "./detectPlatform.js";
import { walletEvents } from "./walletEvents.js";
import { Connection } from "@solana/web3.js";
import { TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID, getMint} from "@solana/spl-token";

const solana = new Connection(_GLOBALS.SolanaRpc);
const appUrlBase = _GLOBALS.BaseUrl;
const appUrl = appUrlBase;

async function processTokenAccounts(tokenAccounts) {
  for (const [i, account] of tokenAccounts.entries()) {
      const parsedAccountInfo = account.account.data;
      const mintAddress = parsedAccountInfo?.parsed?.info?.mint;
      const tokenBalanceUI = parsedAccountInfo?.parsed?.info?.tokenAmount?.uiAmountString;
      const tokenBalance = parsedAccountInfo?.parsed?.info?.tokenAmount?.amount;

      if(_GLOBALS.DebugMode) {
        console.log(`Token Account No: ${i + 1} : ${account.pubkey.toString()}`);
        console.log(`--Token Mint ID: ${mintAddress}`);
        console.log(`--Token Amount UI: ${tokenBalanceUI}`);
        console.log(`--Token Amount: ${tokenBalance}`);
    }

      if (mintAddress) {
          try {
              const tokenMeta = await getTokenMetadata(mintAddress); // Ensure `getTokenMeta` is async
              if(_GLOBALS.DebugMode) console.log(tokenMeta);
          } catch (error) {
              console.error(`Failed to fetch metadata for mint ${mintAddress}:`, error);
          }
      }
  }
}

export async function getTokens(publicKey){
  const filters = [
    { dataSize : 165},
    {
      memcmp : {
      offset: 32,
      bytes: publicKey
    }
  }
  ];
  const tokenAccounts = await solana.getParsedProgramAccounts(
    TOKEN_PROGRAM_ID,
    {filters}
  );

  processTokenAccounts(tokenAccounts);
}

const createWalletDeepLink = (baseURL, buildURLFn) => ({
  baseURL,
  buildURL: buildURLFn,
});

const walletDeepLinks = {
  Phantom: createWalletDeepLink("https://phantom.app/ul/browse/", (appUrlBase, appUrl) => {
    const ref = encodeURIComponent(appUrlBase);
    const url = encodeURIComponent(appUrl);
    return `${walletDeepLinks.Phantom.baseURL}${url}?ref=${ref}`;
  }),
  Coinbase: createWalletDeepLink("https://go.cb-w.com/dapp", (appUrlBase, appUrl) => {
    const queryParams = new URLSearchParams({
      cb_url: encodeURIComponent(appUrl),
    }).toString();
    return `${walletDeepLinks.Coinbase.baseURL}?${queryParams}`;
  }),
  Solflare: createWalletDeepLink("https://solflare.com/ul/v1/browse/", (appUrlBase, appUrl) => {
    const params = new URLSearchParams({
      ref: encodeURIComponent(appUrlBase),
    }).toString();
    const url = encodeURIComponent(appUrl);
    return `${walletDeepLinks.Solflare.baseURL}${url}?${params}`
  }),
};

function openDeepLink(wallet, appUrlBase, appUrl) {
  const walletConfig = walletDeepLinks[wallet];

  if (!walletConfig) {
    if(_GLOBALS.DebugMode) logit(`Unsupported wallet: ${wallet}`);
    return;
  }
  
  if(_GLOBALS.ReferralProgramModule) appUrl += '?rp=1';
  if(_GLOBALS.ReferralProgramActive) appUrl += `?ref=${_GLOBALS.ReferralProgram}`;
    
  let fullURL = walletConfig.buildURL(appUrlBase, appUrl);
  logit(' --- FULL URL --- ');
  logit(fullURL);
  if(_GLOBALS.DebugMode) logit(`Deeplink URL: ${wallet}`);
  
  window.open(fullURL, "_blank");
}

async function connectToWallet(walletName) {
  //try {
  let publicKey = "";
  switch (walletName) {
    case "Coinbase":
      if (window.coinbaseSolana) {
        try {
            await window.coinbaseSolana.connect();
        } catch ( err ) {
            throw new WalletConnectError("User rejected request");
        }
        
        publicKey = window.coinbaseSolana.publicKey.toString();
        if (!publicKey) throw new Error("Public key not found");
        return publicKey;
      } else {
        throw new WalletNotFoundError("Wallet not detected");
      }
      break;

    case "Phantom":
      if (window.phantom?.solana) {
        try {  
            await window.phantom.solana.connect();
        } catch ( err ) {
            throw new WalletConnectError("User rejected request");
        }
        publicKey = window.phantom.solana.publicKey.toString();
        if (!publicKey) throw new Error("Public key not found");
        return publicKey;
      } else {
        throw new WalletNotFoundError("Wallet not detected");
      }
      break;
      
    case "Solflare":
      if (window.solflare?.isSolflare) {
        try {  
            await window.solflare.connect();
        } catch ( err ) {
            throw new WalletConnectError("User rejected request");
        }
        publicKey = window.solflare.publicKey.toString();
        if (!publicKey) throw new Error("Public key not found");
        return publicKey;
      } else {
        throw new WalletNotFoundError("Wallet not detected");
      }
      break;

    default:
      throw new Error(`Unknown wallet: ${walletName}`);
  }

  //logit(`Connected to ${walletName} with publicKey: ${publicKey}`);
  //return publicKey;
  //} catch (error) {
  //  logit(`Error connecting to ${walletName}: ${error.message}`);
  //  return null;
  // }
}

export async function connectWallet(wallet) {
  const platform = detectPlatform();
  if(_GLOBALS.DebugMode) logit(`connectWallet platform(${platform})`);

  /* inside a normal desktop browser */
  if (platform === "Other") {
    walletEvents.triggerWalletConnecting();
    if (wallet.detected) {
      if(_GLOBALS.DebugMode) logit("We are inside browser; trigger browser connect");
      try {
        const publicKey = await connectToWallet(wallet.name);
        walletEvents.triggerWalletConnected({ publicKey });
        return publicKey;
      } catch (error) {
          console.log("connectWallet() error.status", error.code);
          walletEvents.triggerWalletConnectionFailed({ error: error.message });
          throw Error;
      }
    } else {
      walletEvents.triggerWalletConnectionFailed({ error: "Wallet not found" });
    }
  } else {
    //inside in app brwoser
    if (wallet.detected) {
      if(_GLOBALS.DebugMode) logit("We are inside app; trigger browser connect");
      walletEvents.triggerWalletConnecting();
      try {
        const publicKey = await connectToWallet(wallet.name);
        walletEvents.triggerWalletConnected({ publicKey });
        return publicKey;
      } catch (error) {
          walletEvents.triggerWalletConnectionFailed({ error: error.message });
          throw error;
      }
    } else {
      //inside app browser trigger deep link
      if(_GLOBALS.DebugMode) {
        logit("We are inside wallet browser; trigger deep link");
        console.log("wallet.name", wallet.name);
        console.log("appUrlBase", appUrlBase);
        console.log("appUrl", appUrl);
    }
      openDeepLink(wallet.name, appUrlBase, appUrl);
    }
  }
}
